<template>
  <div class="day-table" data-testid="day-table-root">
    <div class="table-header-row">
      <h3
        data-testid="day-table-title"
        class="h4 card-secondary-header"
        @click="close"
      >
        {{ monthString }}
      </h3>
      <div class="direction-buttons">
        <AlButton
          :disabled="!allowPrevious"
          variant="secondary"
          @click="previousMonth"
          data-testid="day-table-back"
          id="day-table-previous-date"
        >
          <i class="fas fa-chevron-left"></i>
        </AlButton>
        <div class="date-display" data-testid="day-table-date">
          {{ dayString }}
        </div>
        <AlButton
          data-testid="day-table-forward"
          :disabled="!allowNext"
          variant="secondary"
          @click="nextMonth"
          id="day-table-next-date"
        >
          <i class="fas fa-chevron-right"></i>
        </AlButton>
      </div>
      <div></div>
    </div>
    <ag-grid-vue
      class="ag-theme-alpine"
      :columnDefs="columnDefs"
      :rowData="rowData"
      :groupDisplayType="'groupRows'"
      :groupDefaultExpanded="1"
      :groupRowRendererParams="groupRowRendererParams"
      :suppressColumnVirtualisation="true"
      :defaultColDef="defaultColDef"
      @grid-ready="onGridReady"
      @newColumnsLoaded="newColumnsLoaded"
    >
    </ag-grid-vue>
  </div>
</template>

<script>
import "ag-grid-enterprise";
import { AgGridVue } from "ag-grid-vue";
import {
  Translator,
  CapacityPlannerMonthlyView,
} from "@agyletime/wfm-vue-common";
import { AlButton } from "@playvox/almendra-components";
import { adjustAvailableForTask } from "../../../utils/intervalUtils";
import { utcToZonedTime } from "date-fns-tz";

export default {
  name: "MonthTable",
  mixins: [Translator],
  props: {
    configurationId: {
      type: String,
      required: true,
    },
    timezone: {
      type: String,
      required: true,
    },
    day: {
      type: Object,
      required: true,
    },
    unitOfWork: {
      type: String,
      required: true,
    },
    allowPrevious: {
      type: Boolean,
      required: true,
    },
    allowNext: {
      type: Boolean,
      required: true,
    },
    workloadTasks: {
      type: Map,
      required: true,
    },
  },
  watch: {
    day() {
      this.getTableData();
      this.handleLoading();
    },
  },
  created() {
    this.getTableData();
  },
  data() {
    return {
      columnDefs: null,
      rowData: null,
      loading: true,
    };
  },
  components: {
    AgGridVue,
    AlButton,
  },
  methods: {
    featureEnabled(key) {
      return window.PlayvoxWFM.featureflow.evaluate(key).isOn();
    },
    handleLoading() {
      if (this.gridApi) {
        if (this.dayData.length === 0) {
          this.gridApi.showLoadingOverlay();
        } else {
          this.gridApi.hideOverlay();
        }
      }
    },
    async getTableData() {
      await this.getDailyCapacityForMonth();
      this.hydrateCapacityWithTasks();
      this.setTableData();
      this.handleLoading();
    },
    async getDailyCapacityForMonth() {
      this.loading = true;
      this.dayData = [];
      this.handleLoading();
      try {
        const result = await CapacityPlannerMonthlyView.getHourlyCapacityForDay(
          this.configurationId,
          this.day.date
        );
        this.dayData = result;
      } catch (error) {
        console.error(JSON.stringify(error));
        this.dayData = [];
      } finally {
        this.loading = false;
        window.dispatchEvent(new Event("resize")); // * Resize screen to fit chart and show x-axis correctly
      }
    },
    hydrateCapacityWithTasks() {
      this.dayData = this.dayData.map((hour) => {
        return {
          ...hour,
          workloadTasks: hour.workloadTasks?.map((task) => {
            return {
              ...task,
              task: this.workloadTasks.get(task.workloadTaskId),
            };
          }),
        };
      });
    },
    setTableData() {
      this.columnDefs = [
        {
          field: "task.name",
          rowGroup: true,
          cellRenderer: "agGroupCellRenderer",
          hide: true,
          columnGroupShow: "open",
        },
        {
          headerName: this.translate("workload-task-name"),
          field: "type",
          menuTabs: this.menuTabs,
          width: 130,
          flex: 0,
          resizable: false,
          suppressSizeToFit: true,
          pinned: "left",
          type: "rightAligned",
        },
        // One column for every hour of the day
        ...this.dayData.map((hour, index) => {
          return {
            headerName: this.getValueInLocale(
              utcToZonedTime(hour.date, this.timezone),
              {
                hour: "2-digit",
                minute: "2-digit",
              }
            ),
            valueGetter: (params) => {
              if (!params.data) {
                return "";
              }

              return params.data?.hours[index];
            },
            menuTabs: this.menuTabs,
            type: "numericColumn",
            flex: 1,
          };
        }),
      ];

      this.rowData = this.getRowData();
      this.sizeToFit();
      this.handleLoading();
    },
    getRowData() {
      const workloadsForAllHours = this.dayData
        .flatMap((hour) => hour.workloadTasks)
        .map((task) => task?.task)
        .filter(Boolean);

      const uniqueWorkloadsById = workloadsForAllHours.reduce((acc, task) => {
        acc.set(task.id, task);
        return acc;
      }, new Map());

      let uniqueWorkloads = Array.from(uniqueWorkloadsById.values());

      // sort by name prop
      uniqueWorkloads.sort((a, b) => {
        return a.name.localeCompare(b.name);
      });

      // Map unique workloads to their daily values
      return uniqueWorkloads.flatMap((workload) => {
        const workloads = [
          {
            task: workload,
            type: this.translate("monthly-summary-available"),
            hours: this.getDailyProp(workload, "available", (task) =>
              adjustAvailableForTask({
                task,
                unitOfWork: this.unitOfWorkProp,
              }).toFixed(1)
            ),
          },
          {
            task: workload,
            type: this.translate("monthly-summary-staffing"),
            hours: this.getDailyProp(
              workload,
              "available",
              (task) =>
                adjustAvailableForTask({
                  task,
                  unitOfWork: this.unitOfWorkProp,
                }).toFixed(1),
              true
            ),
          },
          {
            task: workload,
            type: this.translate("monthly-summary-required"),
            hours: this.getDailyProp(workload, "required"),
          },
          {
            task: workload,
            type: this.translate("monthly-summary-difference"),
            hours: this.getDailyProp(workload, "difference", (task) => {
              const required = task?.required?.[this.unitOfWorkProp] || 0;
              const availableAdjusted = adjustAvailableForTask({
                task,
                unitOfWork: this.unitOfWorkProp,
              });
              const staffing = task?.staffing?.[this.unitOfWorkProp] || 0;

              if (availableAdjusted === 0 && required === 0 && staffing === 0) {
                return 0;
              }
              return (availableAdjusted + staffing - required).toFixed(1);
            }),
          },
        ];

        if (this.featureEnabled("capacity-planner-forecast-sla")) {
          workloads.push({
            task: workload,
            type: this.translate("monthly-summary-forecast"),
            hours: this.getDailyProp(workload, "forecast", (task) => {
              if (typeof task?.sla === "undefined") return "-";
              return ((task.sla || 0) * 100).toFixed(0);
            }),
          });
        }

        return workloads;
      });
    },
    getDailyProp(workload, prop, calc, isAdjustment = false) {
      return this.dayData.map((day) => {
        const workloadTask = day.workloadTasks?.find(
          (task) =>
            task.task.id === workload.id && task.isAdjustment === isAdjustment
        );

        return calc
          ? calc(workloadTask)
          : this.getTaskValue(workloadTask, prop);
      });
    },
    getTaskValue(task, prop) {
      const value = task?.[prop]?.[this.unitOfWorkProp] || 0;
      return value ? value.toFixed(1) : 0;
    },
    sizeToFit() {
      this.gridColumnApi?.autoSizeAllColumns();
    },
    newColumnsLoaded() {
      this.sizeToFit();
      this.handleLoading();
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.sizeToFit();
    },
    previousMonth() {
      this.$emit("back");
    },
    nextMonth() {
      this.$emit("forward");
    },
    close() {
      this.$emit("close");
    },
  },
  computed: {
    unitOfWorkProp() {
      return this.unitOfWork === "FTE" ? "fte" : "hours";
    },
    valueOrZero() {
      const prop = this.unitOfWorkProp;
      return (value) => {
        if (value[prop]) {
          return value[prop];
        } else {
          return 0;
        }
      };
    },
    defaultColDef() {
      return { flex: 1, minWidth: 60, resizable: true };
    },
    menuTabs() {
      return [];
    },
    monthString() {
      return this.getValueInLocale(
        utcToZonedTime(this.day.date, this.timezone),
        {
          month: "long",
        }
      );
    },
    dayString() {
      return this.getValueInLocale(
        utcToZonedTime(this.day.date, this.timezone),
        {
          day: "2-digit",
          month: "long",
          year: "numeric",
        }
      );
    },
    groupRowRendererParams() {
      return {
        suppressCount: true,
        suppressDoubleClickExpand: true,
        suppressEnterExpand: true,
      };
    },
  },
};
</script>

<style lang="scss">
@import "ag-grid-community/dist/styles/ag-grid.css";
@import "ag-grid-community/dist/styles/ag-theme-alpine.css";

.day-table .ag-theme-alpine {
  height: 400px;
}

/* Hide the collapse chevron */
.ag-root-wrapper .ag-row > .ag-row-group > .ag-group-expanded {
  display: none;
}
</style>
<style scoped>
.direction-buttons {
  display: flex;
  justify-content: space-around;
  align-items: center;
  max-width: 26rem;
}

.date-display {
  font-size: 1.5em;
  font-weight: bold;
}
.card-secondary-header {
  cursor: pointer;
  text-decoration: underline;
}
</style>
